import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import classNames from "classnames";
import CardActionArea from "@mui/material/CardActionArea";
import CardHeader from "@mui/material/CardHeader";
import Link from "arena/components/Link";
import ThumbnailCardMedia from "arena/components/ThumbnailCardMedia";
import Card from "@mui/material/Card";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { truncate } from "core/utils/string";

import { isTelegramMiniApp } from "core/utils/telegram";
import StarsIcon from "core/components/StarsIcon";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

const PREFIX = "ContentGridItem";

const classes = {
  avatar: `${PREFIX}-avatar`,
  cardActionArenaInside: `${PREFIX}-cardActionArenaInside`,
  mediaLink: `${PREFIX}-mediaLink`,
  media: `${PREFIX}-media`,
  cardHeader: `${PREFIX}-cardHeader`,
  cardHeaderInside: `${PREFIX}-cardHeaderInside`,
  cardMediaInside: `${PREFIX}-cardMediaInside`,
  link: `${PREFIX}-link`,
  linkSmall: `${PREFIX}-linkSmall`,
  premiumButton: `${PREFIX}-premiumButton`,
};

const Root = styled(Card)(({ theme }) => ({
  width: "100%",

  [`& .${classes.premiumButton}`]: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "white",
    borderRadius: 4,
    height: 20,
    width: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  [`& .${classes.mediaLink}`]: {
    height: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    color: "inherit",
    // paddingBottom: 8,
    [theme.breakpoints.up("sm")]: {
      borderRadius: 4, // desktop
    },
  },

  // [`& .${classes.cardActionArenaInside}`]: {
  //   paddingBottom: 0,
  // },

  [`& .${classes.cardHeader}`]: {
    width: "100%",
    alignItems: "flex-start",
    // padding: "16px 0",
    // paddingLeft: theme.spacing(2),
    // paddingRight: theme.spacing(2),
    // [theme.breakpoints.up("sm")]: {
    //   paddingLeft: theme.spacing(0),
    //   paddingRight: theme.spacing(0),
    // },
  },

  [`& .${classes.cardHeaderInside}`]: {
    position: "absolute",
  },

  [`& .${classes.link}`]: {
    textDecoration: "none",
    fontWeight: 500,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",

    fontSize: "0.9rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.1rem",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.1rem",
    },
  },

  [`& .${classes.linkSmall}`]: {
    fontSize: "0.9rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.9rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.9rem",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "0.9rem",
    },
  },
}));

const DEFAULT = "DEFAULT";
const HIGH = "HIGH";
const MAXRES = "MAXRES";
const MEDIUM = "MEDIUM";

const StyledThumbnailCardMedia = styled(ThumbnailCardMedia)(({ theme }) => ({
  width: "100%",
  position: "relative",
  height: 0,
  paddingTop: "56.25%", // 16:9
  backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.07)",
  overflow: "hidden",
  borderRadius: 0, // mobile
  [theme.breakpoints.up("sm")]: {
    borderRadius: 4, // desktop
  },
  [`&.${classes.cardMediaInside}`]: {
    position: "relative",
    display: "flex",
    alignItems: "end",
  },
  [`&.${classes.cardMediaInside}:before`]: {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    pointerEvents: "none",
    backgroundImage:
      theme.palette.mode === "dark"
        ? `linear-gradient(rgba(0,0,0,0.2),  #000)`
        : `linear-gradient(rgba(255,255,255,0.2),  #fff)`,
  },
}));

// Компонент для портретных превью digital assets (2:3 пропорция)
const StyledPortraitThumbnailCardMedia = styled(ThumbnailCardMedia)(
  ({ theme }) => ({
    width: "100%",
    position: "relative",
    height: 0,
    paddingTop: "150%", // 2:3 пропорция (3/2 = 1.5 = 150%)
    backgroundColor:
      theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.07)",
    overflow: "hidden",
    borderRadius: 0, // mobile
    [theme.breakpoints.up("sm")]: {
      borderRadius: 4, // desktop
    },
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",

    [`&.${classes.cardMediaInside}`]: {
      position: "relative",
      display: "flex",
      alignItems: "end",
    },
    [`&.${classes.cardMediaInside}:before`]: {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      pointerEvents: "none",
      backgroundImage:
        theme.palette.mode === "dark"
          ? `linear-gradient(rgba(0,0,0,0.2),  #000)`
          : `linear-gradient(rgba(255,255,255,0.2),  #fff)`,
    },
  })
);

export default function Item({
  digitalAsset,
  smallTitle,
  variant,
  showDescription,
  previewMode,
}) {
  const { t } = useTranslation("arena");

  // Для digital assets в портретном режиме нужны пропорции 2:3 (не 9:16 как для шортсов)
  const shouldUsePortraitLayout = previewMode === "VERTICAL";
  let thumbnailProps = {};

  if (shouldUsePortraitLayout) {
    // Портретный лейаут (2:3) для digital assets - используем лучшие доступные превью
    const verticalThumbnails = digitalAsset.verticalPreview?.thumbnails;
    const horizontalThumbnails = digitalAsset.preview?.thumbnails;

    if (verticalThumbnails?.length > 0) {
      // Есть вертикальные превью - используем их для портретного отображения
      thumbnailProps = { thumbnails: verticalThumbnails };
    } else if (horizontalThumbnails?.length > 0) {
      // Нет вертикальных, используем горизонтальные с обрезкой для портретного режима
      thumbnailProps = { thumbnails: horizontalThumbnails };
    } else {
      // Нет превью - пустой массив
      thumbnailProps = { thumbnails: [] };
    }
  } else {
    // Горизонтальный лейаут (16:9) - используем лучшие доступные превью
    const horizontalThumbnails = digitalAsset.preview?.thumbnails;
    const verticalThumbnails = digitalAsset.verticalPreview?.thumbnails;

    if (horizontalThumbnails?.length > 0) {
      // Есть горизонтальные превью - используем их
      thumbnailProps = { thumbnails: horizontalThumbnails };
    } else if (verticalThumbnails?.length > 0) {
      // Нет горизонтальных, но есть вертикальные - используем их в горизонтальном блоке
      thumbnailProps = { thumbnails: verticalThumbnails };
    } else {
      // Нет превью - пустой массив
      thumbnailProps = { thumbnails: [] };
    }
  }

  // Выбираем правильный компонент для отображения
  const MediaComponent = shouldUsePortraitLayout
    ? StyledPortraitThumbnailCardMedia // 2:3 для digital assets в портретном режиме
    : StyledThumbnailCardMedia; // 16:9 для обычного режима

  return (
    <Root>
      <CardActionArea
        component={Link}
        to={digitalAsset.url}
        className={classNames(classes.mediaLink, {
          [classes.cardActionArenaInside]: variant === "TITLE_INSIDE",
        })}
      >
        <MediaComponent
          className={classNames({
            [classes.cardMediaInside]: variant === "TITLE_INSIDE",
          })}
          {...thumbnailProps}
        >
          {digitalAsset.premium && (
            <Typography
              classes={{
                root: classNames(classes.premiumButton),
              }}
              variant="caption"
              color="textPrimary"
              sx={{
                bgcolor: digitalAsset.viewerPurchase
                  ? "background.body"
                  : "primary.main",
              }}
            >
              {isTelegramMiniApp ? (
                <>
                  {digitalAsset.viewerPurchase ? (
                    <ShoppingBasketIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <StarsIcon
                      sx={{
                        fontSize: "13px",
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  {digitalAsset.viewerPurchase ? (
                    <LockOpenIcon sx={{ fontSize: "16px" }} />
                  ) : (
                    <LockIcon sx={{ fontSize: "16px" }} />
                  )}
                </>
              )}
            </Typography>
          )}

          {variant === "TITLE_INSIDE" && (
            <CardHeader
              className={classNames({
                [classes.cardHeader]: true,
                [classes.cardHeaderInside]: true,
              })}
              title={truncate(digitalAsset.title, smallTitle ? 72 : 80)}
              subheader={t("publishAt", { date: digitalAsset.publishAt })}
              titleTypographyProps={{
                color: "textPrimary",
                className: classNames({
                  [classes.link]: true,
                  [classes.linkSmall]: smallTitle,
                }),
              }}
              subheaderTypographyProps={{
                variant: "caption",
                color: "textSecondary",
              }}
            />
          )}
        </MediaComponent>

        {variant === "TITLE_OUTSIDE" && (
          <>
            <CardHeader
              className={classes.cardHeader}
              title={digitalAsset.title}
              subheader={showDescription ? digitalAsset.description : null}
              titleTypographyProps={{
                gutterBottom: true,
                className: classNames({
                  [classes.link]: !smallTitle,
                  [classes.linkSmall]: smallTitle,
                }),
              }}
              subheaderTypographyProps={{
                variant: "body2",
                color: "textSecondary",
              }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ pl: 2, pr: 2, pb: 2, mt: "auto" }}
            >
              {t("publishAt", { date: digitalAsset.publishAt })}
            </Typography>
          </>
        )}
      </CardActionArea>
    </Root>
  );
}

Item.propTypes = {
  digitalAsset: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    publishAt: PropTypes.string.isRequired,
    premium: PropTypes.bool,
    viewerPurchase: PropTypes.object,
    preview: PropTypes.shape({
      url: PropTypes.string,
      thumbnails: PropTypes.arrayOf(
        PropTypes.shape({
          format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
          url: PropTypes.string.isRequired,
          webpUrl: PropTypes.string,
        })
      ),
    }),
    verticalPreview: PropTypes.shape({
      url: PropTypes.string,
      thumbnails: PropTypes.arrayOf(
        PropTypes.shape({
          format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
          url: PropTypes.string.isRequired,
          webpUrl: PropTypes.string,
        })
      ),
    }),
    url: PropTypes.string.isRequired,
  }).isRequired,
  showDescription: PropTypes.bool,
  variant: PropTypes.oneOf(["TITLE_INSIDE", "TITLE_OUTSIDE"]),
  smallTitle: PropTypes.bool,
  previewMode: PropTypes.oneOf(["HORIZONTAL", "VERTICAL"]),
};
